import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect, useRef } from "react";
import { User, Plus } from "lucide-react";
import "flag-icons/css/flag-icons.min.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { countries, countryCodeMap } from "@/data/countries";
import { CreateCompanyModal } from "@/components/companies/CreateCompanyModal";
import { CustomerStatus } from "@/types/crm";
import { useToast } from "@/hooks/use-toast";
import { companyAPI, CreateCompanyData } from "@/services/companyService";
import { useAuth } from "@/hooks/useAuth";

interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: any) => Promise<void> | void;
  companies?: any[];
  existingContact?: any;
  customerStatuses?: CustomerStatus[];
  existingContacts?: any[];
}

export function CreateContactModal({
  isOpen,
  onClose,
  onSave,
  companies = [],
  existingContact,
  customerStatuses = [],
  existingContacts = [],
}: CreateContactModalProps) {
  const { toast } = useToast();
  const { company } = useAuth();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "",
    position: "",
    companyId: "",
    linkedinUrl: "",
    notes: "",
    statusId: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [localCompanies, setLocalCompanies] = useState<any[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  const fetchCompanies = async () => {
    if (!company?.slug) return;
    setIsLoadingCompanies(true);
    try {
      const response = await companyAPI.getCompanies({
        slug: company.slug,
        limit: 1000,
      });
      if (response.data) {
        setLocalCompanies(response.data);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      countryCode: "",
      position: "",
      companyId: "",
      linkedinUrl: "",
      notes: "",
      statusId: "",
    });
    setErrors({});
    setSearchTerm("");
    setShowDropdown(false);
  };

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen, company?.slug]);

  useEffect(() => {
    if (existingContact) {
      let cleanPhone = existingContact.phone || "";
      let code = existingContact.countryCode || "";

      if (code && cleanPhone.startsWith(code)) {
        cleanPhone = cleanPhone.substring(code.length).trim();
      } else if (!code && cleanPhone.startsWith("+")) {
        const matchedCountry = countries.find((c) =>
          cleanPhone.startsWith(c.code),
        );
        if (matchedCountry) {
          code = matchedCountry.code;
          cleanPhone = cleanPhone.substring(code.length).trim();
        }
      }

      setFormData({
        firstName: existingContact.firstName || "",
        lastName: existingContact.lastName || "",
        email: existingContact.email || "",
        phone: cleanPhone,
        countryCode: code,
        position: existingContact.position || "",
        companyId: existingContact.company?.id || "",
        linkedinUrl: existingContact.linkedinUrl || "",
        notes: existingContact.notes || "",
        statusId: existingContact.statusId || "",
      });

      const country = countries.find((c) => c.code === code);
      setSearchTerm(country ? `${country.name} (${country.code})` : code || "");
    } else if (!isOpen) {
      resetForm();
    }
  }, [existingContact, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "El nombre es obligatorio";
    if (formData.email.trim()) {
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Ingrese un email válido";
      } else {
        const isDuplicate = existingContacts.some(
          (contact) =>
            contact.email &&
            contact.email.toLowerCase() === formData.email.toLowerCase() &&
            contact.id !== existingContact?.id,
        );
        if (isDuplicate) {
          newErrors.email = "Ya existe un contacto con este email";
        }
      }
    }
    if (formData.phone.trim() && formData.countryCode) {
      const country = countries.find((c) => c.code === formData.countryCode);
      if (country) {
        const libPhoneCountry = countryCodeMap[country.flagCode];
        const phoneNumber = parsePhoneNumberFromString(
          formData.phone,
          libPhoneCountry as any,
        );
        if (!phoneNumber || !phoneNumber.isValid()) {
          newErrors.phone = "Número inválido para el país seleccionado";
        }
      }
    } else if (formData.phone.trim() && !formData.countryCode) {
      newErrors.countryCode = "Seleccione un país para el teléfono";
    }

    if (!formData.companyId.trim())
      newErrors.companyId = "Seleccione una empresa";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast({
        title: "Error de validación",
        description: "Por favor, completa todos los campos obligatorios",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const selectedCompany = localCompanies.find(
      (c) => c.id === formData.companyId,
    );

    let finalStatusId = formData.statusId;
    if (!finalStatusId && customerStatuses.length > 0) {
      const activeStatus = customerStatuses.find((s) =>
        s.name.toLowerCase().includes("activo"),
      );
      finalStatusId = activeStatus ? activeStatus.id : customerStatuses[0].id;
    }

    const selectedStatus = customerStatuses.find((s) => s.id === finalStatusId);

    const fullName = `${formData.firstName} ${formData.lastName}`.trim();

    const selectedCountryData = countries.find(
      (c) => c.code === formData.countryCode,
    );
    const formattedPhone = selectedCountryData
      ? `${selectedCountryData.code} ${formData.phone}`
      : formData.phone;

    const newContact = {
      id: existingContact?.id || Date.now().toString(),
      firstName: formData.firstName,
      lastName: formData.lastName,
      name: fullName,
      email: formData.email.trim() || null,
      phone: formData.phone.trim() ? formattedPhone : null,
      countryCode: formData.countryCode || null,
      position: formData.position.trim() || null,
      companyId: formData.companyId || null,
      client_company_id: formData.companyId || null,
      contact_status_id: finalStatusId,
      statusId: finalStatusId,
      linkedinUrl: formData.linkedinUrl.trim() || null,
      notes: formData.notes.trim() || null,
      company: selectedCompany,
      status: selectedStatus?.name,
      opportunities: existingContact?.opportunities || [],
      activities: existingContact?.activities || [],
      attachments: [],
      createdAt: existingContact?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await onSave(newContact);
    resetForm();
    onClose();
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCountryInputChange = (value: string) => {
    setSearchTerm(value);
    setShowDropdown(true);
  };

  const filteredCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code.includes(searchTerm),
  );

  const handleSelectCountry = (country: (typeof countries)[0]) => {
    setFormData((prev) => ({ ...prev, countryCode: country.code }));
    setSearchTerm(`${country.name} (${country.code})`);
    setShowDropdown(false);
  };

  const handleCreateCompany = async (companyData: CreateCompanyData) => {
    try {
      const response = await companyAPI.createCompany(companyData);
      await fetchCompanies();
      if (response.data?.id) {
        setFormData((prev) => ({ ...prev, companyId: response.data.id }));
      }

      toast({
        title: "Empresa creada",
        description: "La empresa ha sido creada exitosamente",
      });

      setIsCompanyModalOpen(false);
    } catch (error: any) {
      if (
        error.message?.includes("Error al configurar") ||
        error.message?.includes("estructura") ||
        error.message?.includes("exitosamente") ||
        error.message?.includes("creada") ||
        error.message?.includes("success")
      ) {
        try {
          await fetchCompanies();
          toast({
            title: "Empresa creada",
            description: "La empresa ha sido creada exitosamente",
          });
          setIsCompanyModalOpen(false);
          return;
        } catch (refreshError) {
          console.error("Error al refrescar:", refreshError);
        }
      }

      toast({
        title: "Error",
        description: error.message || "No se pudo crear la empresa",
        variant: "destructive",
      });
      throw error;
    }
  };

  const selectedCountry = countries.find(
    (c) => c.code === formData.countryCode,
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-2xl">
              <User className="h-6 w-6 mr-2 text-blue-600" />
              {existingContact ? "Editar Contacto" : "Crear Nuevo Contacto"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Nombre *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  placeholder="Juan"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm">{errors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Apellido</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  placeholder="Pérez"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm">{errors.lastName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="juan.perez@empresa.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <div className="flex gap-2 relative">
                  <div className="relative w-40">
                    <Input
                      ref={inputRef}
                      value={searchTerm}
                      onChange={(e) => handleCountryInputChange(e.target.value)}
                      placeholder="Buscar país o código"
                    />
                    {showDropdown && (
                      <div className="absolute z-50 bg-white border mt-1 max-h-60 overflow-y-auto w-full shadow-lg rounded">
                        {filteredCountries.map((c) => (
                          <div
                            key={c.code}
                            className="cursor-pointer px-2 py-1 hover:bg-gray-100 flex items-center gap-2"
                            onClick={() => handleSelectCountry(c)}
                          >
                            <span
                              className={`fi fi-${c.flagCode} w-6 h-4`}
                            ></span>
                            <span>
                              {c.name} ({c.code})
                            </span>
                          </div>
                        ))}
                        {filteredCountries.length === 0 && (
                          <div className="px-2 py-1 text-gray-400">
                            No se encontró ningún país
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) =>
                      handleChange("phone", e.target.value.replace(/\D/g, ""))
                    }
                    placeholder={
                      selectedCountry
                        ? selectedCountry.example
                        : "Número de teléfono"
                    }
                    className="flex-1"
                  />
                </div>
                {errors.countryCode && (
                  <p className="text-red-500 text-sm">{errors.countryCode}</p>
                )}
                {errors.phone && (
                  <p className="text-red-500 text-sm">{errors.phone}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="position">Cargo</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => handleChange("position", e.target.value)}
                  placeholder="Gerente de Ventas"
                />
                {errors.position && (
                  <p className="text-red-500 text-sm">{errors.position}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Empresa *</Label>
                <div className="flex gap-2">
                  <Select
                    onValueChange={(value) => handleChange("companyId", value)}
                    value={formData.companyId}
                    disabled={isLoadingCompanies}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue
                        placeholder={
                          isLoadingCompanies
                            ? "Cargando empresas..."
                            : "Seleccionar empresa"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.isArray(localCompanies) &&
                        localCompanies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setIsCompanyModalOpen(true)}
                    className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700 shrink-0"
                    title="Crear nueva empresa"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {errors.companyId && (
                  <p className="text-red-500 text-sm">{errors.companyId}</p>
                )}
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  placeholder="Información adicional sobre el contacto..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {existingContact ? "Actualizar Contacto" : "Crear Contacto"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CreateCompanyModal
        isOpen={isCompanyModalOpen}
        onClose={() => setIsCompanyModalOpen(false)}
        onSave={handleCreateCompany}
        companySlug={company?.slug || ""}
      />
    </>
  );
}
